import React, { useEffect, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { getRemetente, saveRemetente, buscarCep, enviarLogo, verLogo, removerLogo } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { readPending } from '../../lib/pendingEnvio';

const vazio = { nome: '', cpf_cnpj: '', telefone: '', email: '', cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '' };

const Campo = ({ label, children }) => (
  <label className="block"><span className="text-sm font-medium text-slate-700">{label}</span>{children}</label>
);

export const LogoSection = () => {
  const { user, refreshUser } = useAuth();
  const [previa, setPrevia] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let url;
    if (user?.tem_logo) {
      verLogo().then((b) => { url = URL.createObjectURL(b); setPrevia(url); }).catch(() => setPrevia(null));
    } else { setPrevia(null); }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [user?.tem_logo]);

  const enviar = async (file) => {
    if (!file) return;
    setBusy(true); setMsg(null);
    try {
      await enviarLogo(file);
      await refreshUser();
      setMsg({ ok: true, text: 'Logo salva. Ela aparece nas próximas etiquetas geradas.' });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setMsg({ ok: false, text: err?.response?.status === 413 ? 'A imagem deve ter no máximo 1 MB.' : (typeof d === 'string' ? d : 'Não foi possível enviar a imagem.') });
    } finally { setBusy(false); }
  };

  const remover = async () => {
    setBusy(true); setMsg(null);
    try { await removerLogo(); await refreshUser(); } finally { setBusy(false); }
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-100 p-6 max-w-2xl mb-6">
      <h2 className="text-lg font-semibold text-slate-900">Logo da etiqueta</h2>
      <p className="text-sm text-slate-500 mb-4">Sua imagem aparece na etiqueta no lugar do logo dos Correios, no canto superior esquerdo. O código de barras, o QR e os dados continuam iguais.</p>
      <div className="flex items-center gap-5">
        <div className="w-28 h-20 rounded-xl border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden flex-shrink-0">
          {previa ? <img src={previa} alt="Sua logo" className="max-w-full max-h-full object-contain" /> : <span className="text-xs text-slate-400 text-center px-2">Sem logo</span>}
        </div>
        <div className="space-y-2">
          <label className="inline-flex items-center px-4 h-9 rounded-full border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer">
            {busy ? 'Enviando...' : (user?.tem_logo ? 'Trocar imagem' : 'Enviar imagem')}
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={busy}
              onChange={(e) => { enviar(e.target.files[0]); e.target.value = ''; }} />
          </label>
          {user?.tem_logo && <button type="button" onClick={remover} disabled={busy} className="block text-xs text-red-600 hover:underline">Remover logo</button>}
          <p className="text-[11px] text-slate-500">PNG, JPG ou WEBP, até 1 MB. Prefira imagem quadrada ou pouco mais larga que alta, com fundo branco ou transparente.</p>
        </div>
      </div>
      {msg && <p className={`text-sm mt-3 ${msg.ok ? 'text-emerald-600' : 'text-red-600'}`}>{msg.text}</p>}
    </section>
  );
};

const RemetenteSection = () => {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [salvou, setSalvou] = useState(false);
  const [f, setF] = useState(vazio);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getRemetente().then((r) => {
      if (r && r.nome) {
        const e = r.endereco || {};
        setF({ ...vazio, nome: r.nome, cpf_cnpj: r.cpf_cnpj, telefone: r.telefone || '', email: r.email || '', ...e, complemento: e.complemento || '' });
      }
    });
  }, []);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const aoSairCep = async () => {
    const r = await buscarCep(f.cep);
    if (r) setF((cur) => ({ ...cur, logradouro: cur.logradouro || r.logradouro, bairro: cur.bairro || r.bairro, cidade: r.cidade, uf: r.uf }));
  };

  const salvar = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      await saveRemetente({
        nome: f.nome, cpf_cnpj: f.cpf_cnpj, telefone: f.telefone || null, email: f.email || null,
        endereco: { cep: f.cep, logradouro: f.logradouro, numero: f.numero, complemento: f.complemento || null, bairro: f.bairro, cidade: f.cidade, uf: f.uf },
      });
      await refreshUser();
      setMsg({ ok: true, text: 'Dados salvos.' });
      setSalvou(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setMsg({ ok: false, text: typeof d === 'string' ? d : 'Confira os campos obrigatórios.' });
    } finally { setBusy(false); }
  };

  return (
    <>
    <LogoSection />
    <section className="bg-white rounded-2xl border border-slate-100 p-6 max-w-2xl">
      <h2 className="text-lg font-semibold text-slate-900">Meus dados de remetente</h2>
      <p className="text-sm text-slate-500 mb-5">Aparecem na etiqueta e definem a origem dos seus envios (o CEP abaixo).</p>
      <form onSubmit={salvar} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <Campo label="Nome ou razão social *"><Input className="mt-1" value={f.nome} onChange={set('nome')} maxLength={50} required /></Campo>
          <Campo label="CPF ou CNPJ *"><Input className="mt-1" value={f.cpf_cnpj} onChange={set('cpf_cnpj')} required /></Campo>
          <Campo label="Telefone (com DDD)"><Input className="mt-1" value={f.telefone} onChange={set('telefone')} /></Campo>
          <Campo label="E-mail"><Input className="mt-1" type="email" value={f.email} onChange={set('email')} /></Campo>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Campo label="CEP *"><Input className="mt-1" value={f.cep} onChange={set('cep')} onBlur={aoSairCep} required /></Campo>
          <div className="sm:col-span-2"><Campo label="Rua *"><Input className="mt-1" value={f.logradouro} onChange={set('logradouro')} maxLength={50} required /></Campo></div>
          <Campo label="Número *"><Input className="mt-1" value={f.numero} onChange={set('numero')} maxLength={10} required /></Campo>
          <div className="sm:col-span-2"><Campo label="Complemento"><Input className="mt-1" value={f.complemento} onChange={set('complemento')} maxLength={30} /></Campo></div>
          <div className="sm:col-span-3"><Campo label="Bairro *"><Input className="mt-1" value={f.bairro} onChange={set('bairro')} maxLength={30} required /></Campo></div>
          <div className="sm:col-span-2"><Campo label="Cidade *"><Input className="mt-1" value={f.cidade} onChange={set('cidade')} maxLength={30} required /></Campo></div>
          <Campo label="UF *"><Input className="mt-1 uppercase" value={f.uf} onChange={set('uf')} maxLength={2} required /></Campo>
        </div>
        {msg && <p className={`text-sm ${msg.ok ? 'text-emerald-600' : 'text-red-600'}`}>{msg.text}</p>}
        <div className="flex flex-wrap gap-3">
          <Button type="submit" disabled={busy} className="inn-btn-primary rounded-full h-10 px-6">Salvar</Button>
          {salvou && readPending() && (
            <Button type="button" variant="outline" onClick={() => navigate('/calcular')} className="rounded-full h-10 px-6">Continuar meu envio</Button>
          )}
        </div>
      </form>
    </section>
    </>
  );
};

export default RemetenteSection;
